import { NAWL_LEMMAS } from "@/lib/tool-kit/writing-analysis/nawl";
import {
  letterTokens,
  stripPossessive,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Academic vocabulary coverage against the New Academic Word List (NAWL 1.2, Browne,
 * Culligan & Phillips, 2013, CC BY-SA 4.0 — see `nawl.ts` for the source, the licence
 * and the changes made).
 *
 * The NAWL lists the words that are frequent across academic texts but are not among
 * the 2,800 core words of general English in its companion list, the NGSL. So common
 * academic words that are also everyday English — research, data, analysis, theory —
 * are deliberately not on it, and a coverage figure measures the vocabulary beyond them.
 *
 * Matching is by the list's own forms. Its units are "flemmas" — a headword with its
 * inflections, British and American spellings included (randomize, randomise,
 * randomized…) — not whole word families, so "classify" and "classification" are two
 * entries, each counted where the list counts it. Hyphenated compounds are split, as
 * the corpus tools the list was built for split them: "non-linear" is "non" + "linear".
 */

export interface AcademicWordMatch {
  start: number;
  end: number;
  text: string;
  lemma: string;
}

export interface AcademicLemmaRow {
  lemma: string;
  count: number;
  /** The forms of the lemma the text used, in order of first appearance. */
  forms: string[];
}

export interface AcademicVocabularyReport {
  /** Running words: letters only, hyphenated compounds split. */
  tokens: number;
  academicTokens: number;
  /** Share of running words on the list, 0–1. */
  coverage: number;
  matches: AcademicWordMatch[];
  /** One row per lemma, most frequent first. */
  lemmas: AcademicLemmaRow[];
}

/** Shown as a lemma but never matched: bare "found" is the past tense of "find". */
const DISPLAY_ONLY = new Set(["found"]);

const RE_LINE_BREAK = /\n/;

function buildIndex(): Map<string, string> {
  const index = new Map<string, string>();
  for (const line of NAWL_LEMMAS.split(RE_LINE_BREAK)) {
    const forms = line.split(",").filter((form) => form.length > 0);
    const lemma = forms[0];
    if (!lemma) {
      continue;
    }
    for (const form of forms) {
      if (!DISPLAY_ONLY.has(form)) {
        index.set(form, lemma);
      }
    }
  }
  return index;
}

const FORM_TO_LEMMA = buildIndex();

/** Number of lemmas on the adapted list — stated on the page next to the attribution. */
export const NAWL_LEMMA_COUNT = new Set(FORM_TO_LEMMA.values()).size;

const RE_COMBINING = /\p{M}/gu;

/** Accents folded the way the list was folded: "élite" → "elite". */
function fold(word: string): string {
  return word.normalize("NFD").replace(RE_COMBINING, "");
}

export function lemmaOf(word: string): string | undefined {
  return FORM_TO_LEMMA.get(fold(stripPossessive(word.toLowerCase())));
}

export function analyzeAcademicVocabulary(
  text: string
): AcademicVocabularyReport {
  const tokens = letterTokens(text);
  const matches: AcademicWordMatch[] = [];
  const rows = new Map<string, AcademicLemmaRow>();
  for (const token of tokens) {
    const lemma = lemmaOf(token.lower);
    if (!lemma) {
      continue;
    }
    matches.push({
      start: token.start,
      end: token.end,
      text: token.text,
      lemma,
    });
    const form = token.lower;
    const row = rows.get(lemma);
    if (row) {
      row.count += 1;
      if (!row.forms.includes(form)) {
        row.forms.push(form);
      }
    } else {
      rows.set(lemma, { lemma, count: 1, forms: [form] });
    }
  }
  return {
    tokens: tokens.length,
    academicTokens: matches.length,
    coverage: tokens.length > 0 ? matches.length / tokens.length : 0,
    matches,
    lemmas: [...rows.values()].sort(
      (a, b) => b.count - a.count || a.lemma.localeCompare(b.lemma)
    ),
  };
}
